import {
  Component,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import AutoSizer from "react-virtualized-auto-sizer";

import { cn } from "@/lib/utils";

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class TableLayoutErrorBoundary extends Component<
  PropsWithChildren<{}>,
  ErrorBoundaryState
> {
  constructor(props: PropsWithChildren<{}>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("TableLayout Error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="p-4 border border-red-300 bg-red-50 rounded-md">
          <h3 className="text-red-800 font-semibold">TableLayout Error</h3>
          <p className="text-red-600 text-sm mt-1">
            {this.state.error?.message || "An error occurred"}
          </p>
          <details className="mt-2">
            <summary className="text-red-700 cursor-pointer">
              Stack trace
            </summary>
            <pre className="text-xs mt-1 text-red-600 overflow-auto">
              {this.state.error?.stack}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

type Props = PropsWithChildren<{
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  autoSize?: boolean;
}>;

export const TableLayout = forwardRef<HTMLDivElement, Props>(
  ({ children, className, autoSize = true }, ref) => {
    const _ref = useRef<HTMLDivElement>(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);
    }, []);

    if (!mounted) {
      return null;
    }

    return (
      <TableLayoutErrorBoundary>
        <TableLayoutInner ref={ref} className={className} autoSize={autoSize}>
          {children}
        </TableLayoutInner>
      </TableLayoutErrorBoundary>
    );
  },
);

const TableLayoutInner = forwardRef<HTMLDivElement, Props>(
  ({ children, className, autoSize = true }, ref) => {
    const _ref = useRef<HTMLDivElement>(null);

    if (!autoSize) {
      return (
        <NonAutoSizedTableLayout ref={ref} className={className}>
          {children}
        </NonAutoSizedTableLayout>
      );
    }

    return (
      <div style={{ flex: "1 1 auto", minHeight: 0, height: "100%" }}>
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => {
            if (height === 0 || width === 0) {
              return null;
            }

            return (
              <div
                className={cn(
                  "rounded-md border overflow-hidden bg-background",
                  className,
                )}
                style={{
                  width: `${width}px`,
                }}
                ref={_ref}
              >
                <div
                  style={{
                    height: `${height}px`,
                    width: `${width}px`,
                    position: "relative",
                    overflow: "auto",
                  }}
                  ref={ref}
                  className={cn(
                    "[&::-webkit-scrollbar]:h-2",
                    "[&::-webkit-scrollbar]:w-0",
                    "[&::-webkit-scrollbar-track:horizontal]:bg-gray-100",
                    "[&::-webkit-scrollbar-thumb:horizontal]:bg-gray-300",
                    "[&::-webkit-scrollbar-thumb:horizontal]:rounded",
                    "[&::-webkit-scrollbar-thumb:horizontal:hover]:bg-gray-400",
                    "scrollbar-thin", // For Firefox
                  )}
                >
                  {children}
                </div>
              </div>
            );
          }}
        </AutoSizer>
      </div>
    );
  },
);

const NonAutoSizedTableLayout = forwardRef<HTMLDivElement, Props>(
  ({ children, className }, ref) => {
    const [height, setHeight] = useState(0);
    const _ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const node = _ref.current;

      if (!node) return;

      const resizeObserver = new ResizeObserver(([e]) => {
        const _height = e.contentRect.height;
        !height && setHeight(_height);
      });

      resizeObserver.observe(node);

      return () => resizeObserver.disconnect();
    }, [height]);

    return (
      <div
        className={cn("flex-1 rounded-md border overflow-hidden", className)}
        ref={_ref}
      >
        <div
          ref={ref}
          className={cn(
            "overflow-y-auto overflow-x-scroll",
            "[&::-webkit-scrollbar]:h-3",
            "[&::-webkit-scrollbar]:w-0",
            "[&::-webkit-scrollbar-track:horizontal]:bg-gray-100",
            "[&::-webkit-scrollbar-thumb:horizontal]:bg-gray-300",
            "[&::-webkit-scrollbar-thumb:horizontal]:rounded",
            "[&::-webkit-scrollbar-thumb:horizontal:hover]:bg-gray-400",
            "scrollbar-thin", // For Firefox
          )}
        >
          {children}
        </div>
      </div>
    );
  },
);
